import type { Enemy } from "@shared/entities";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { AnnotationEditor } from "../components/annotation-editor";
import { EventChip } from "../components/chips";
import { LoadError, Loading, PageHeader } from "../components/page-state";
import { StageLegend, StageStrip, stageEvent, waveCount } from "../components/stage-strip";
import { num, scaledStat } from "../lib/format";
import { chapterQuery, enemiesQuery, gameTableQuery } from "../lib/queries";

export const Route = createFileRoute("/chapters/$id")({ component: ChapterPage });

interface ScalingRules {
	stage_scaling_per_stage: number;
	chapter_multiplier: number;
	chapter_multipliers?: Record<string, number>;
	boss: { hp_mult: number; atk_mult: number };
	mini_boss: { hp_mult: number; atk_mult: number };
}

function ChapterPage() {
	const { id } = Route.useParams();
	const chapter = useQuery(chapterQuery(id));
	const enemies = useQuery(enemiesQuery);
	const rules = useQuery(gameTableQuery("enemies"));

	if (chapter.isPending) return <Loading what="Loading chapter" />;
	if (chapter.isError) return <LoadError error={chapter.error} retry={() => void chapter.refetch()} />;

	const ch = chapter.data;
	const byId = new Map((enemies.data ?? []).map((e) => [e.id, e]));
	const scaling = rules.data?.data as ScalingRules | undefined;
	const miniStage = Number(Object.entries(ch.stage_events).find(([, ev]) => ev === "mini_boss")?.[0] ?? 10);
	const bossStage = Number(Object.entries(ch.stage_events).find(([, ev]) => ev === "boss")?.[0] ?? ch.stages);

	return (
		<div className="space-y-8">
			<PageHeader eyebrow={`Chapter ${ch.index} of 10`} title={ch.annotation.summary || ch.name}>
				{ch.index > 1 && (
					<Link to="/chapters/$id" params={{ id: `chapter_${ch.index - 1}` }} className="btn">
						Previous
					</Link>
				)}
				{ch.index < 10 && (
					<Link to="/chapters/$id" params={{ id: `chapter_${ch.index + 1}` }} className="btn">
						Next
					</Link>
				)}
			</PageHeader>

			<section className="panel p-5">
				<div className="mb-3 flex flex-wrap items-center justify-between gap-3">
					<h2 className="text-base">{ch.stages} stages, nothing random</h2>
					<StageLegend />
				</div>
				<StageStrip chapter={ch} />
				<div className="mt-4 overflow-x-auto">
					<table className="table">
						<thead>
							<tr>
								<th>Stage</th>
								<th>Event</th>
								<th>Wave</th>
								<th>What happens</th>
							</tr>
						</thead>
						<tbody>
							{Array.from({ length: ch.stages }, (_, i) => i + 1)
								.filter((s) => stageEvent(ch, s) !== "battle" || ch.wave_size.some((w) => w.from_stage === s) || s === 1)
								.map((s) => {
									const ev = stageEvent(ch, s);
									return (
										<tr key={s}>
											<td className="num">{s}</td>
											<td>
												<EventChip event={ev} />
											</td>
											<td className="num">{ev === "battle" ? waveCount(ch, s) : ev === "boss" || ev === "mini_boss" ? 1 : "—"}</td>
											<td style={{ color: "var(--color-ink-300)" }}>{describe(ev, ch, s, byId)}</td>
										</tr>
									);
								})}
						</tbody>
					</table>
				</div>
			</section>

			<section className="grid gap-6 lg:grid-cols-2">
				<div className="panel p-5">
					<h2 className="mb-1 text-base">Monsters in the pool</h2>
					<p className="mb-3 text-xs" style={{ color: "var(--color-ink-400)" }}>
						Stats at stage 1 and stage {ch.stages} of this chapter, after chapter and stage scaling.
					</p>
					<EnemyTable ids={ch.enemy_pool} byId={byId} chapterIndex={ch.index} stages={[1, ch.stages]} scaling={scaling} />
				</div>
				<div className="panel p-5">
					<h2 className="mb-1 text-base">Bosses</h2>
					<p className="mb-3 text-xs" style={{ color: "var(--color-ink-400)" }}>
						Boss multipliers on top of that stage's scaling: mini-boss ×{scaling?.mini_boss.hp_mult ?? "…"} HP / ×{scaling?.mini_boss.atk_mult ?? "…"} ATK,
						boss ×{scaling?.boss.hp_mult ?? "…"} HP / ×{scaling?.boss.atk_mult ?? "…"} ATK.
					</p>
					<table className="table">
						<thead>
							<tr>
								<th>Role</th>
								<th>Monster</th>
								<th className="num">Stage</th>
								<th className="num">HP</th>
								<th className="num">ATK</th>
								<th className="num">DEF</th>
							</tr>
						</thead>
						<tbody>
							<BossRow role="Mini-boss" enemy={byId.get(ch.mini_boss_type)} id={ch.mini_boss_type} stage={miniStage} chapterIndex={ch.index} mult={scaling?.mini_boss} scaling={scaling} />
							<BossRow role="Boss" enemy={byId.get(ch.boss_type)} id={ch.boss_type} stage={bossStage} chapterIndex={ch.index} mult={scaling?.boss} scaling={scaling} />
						</tbody>
					</table>
				</div>
			</section>

			<AnnotationEditor type="chapter" id={ch.id} annotation={ch.annotation} />
		</div>
	);
}

function describe(ev: string, ch: { wave_size: Array<{ from_stage: number; count: number }>; enemy_pool: string[] }, stage: number, byId: Map<string, Enemy>): string {
	switch (ev) {
		case "skill_selection":
			return "Pick 1 of 3 skills of one rarity, then the battle.";
		case "boon_selection":
			return "Pick 1 of 4 boons (+10 % ATK / DEF / max HP, or heal 30 %), then the battle.";
		case "mini_boss":
			return "Drops gear scrolls. May be immune to crowd control.";
		case "boss":
			return "Clearing it unlocks the next chapter and banks the chest.";
		default: {
			const n = waveCount(ch, stage);
			const names = ch.enemy_pool.map((id) => byId.get(id)?.name ?? id).join(", ");
			return `${n} ${n === 1 ? "enemy" : "enemies"} from the pool (${names}) from this stage on.`;
		}
	}
}

function EnemyTable({ ids, byId, chapterIndex, stages, scaling }: { ids: string[]; byId: Map<string, Enemy>; chapterIndex: number; stages: number[]; scaling?: ScalingRules }) {
	return (
		<table className="table">
			<thead>
				<tr>
					<th>Monster</th>
					{stages.map((s) => (
						<th key={s} className="num">
							HP · ATK · DEF at {s}
						</th>
					))}
					<th className="num">XP</th>
					<th className="num">Coins</th>
				</tr>
			</thead>
			<tbody>
				{ids.map((id) => {
					const e = byId.get(id);
					if (!e) {
						return (
							<tr key={id}>
								<td colSpan={4} style={{ color: "var(--color-bad)" }}>
									{id} is in the pool but not in enemies.json
								</td>
							</tr>
						);
					}
					return (
						<tr key={id}>
							<td>
								<Link to="/monsters/$id" params={{ id }} className="underline decoration-[var(--color-ink-600)] underline-offset-2 hover:decoration-[var(--color-gold-400)]">
									{e.name}
								</Link>
							</td>
							{stages.map((s) => (
								<td key={s} className="num">
									{scaling ? `${num(scaledStat(e.hp, chapterIndex, s, scaling))} · ${num(scaledStat(e.atk, chapterIndex, s, scaling), 1)} · ${num(scaledStat(e.def, chapterIndex, s, scaling), 1)}` : "…"}
								</td>
							))}
							<td className="num">{e.xp}</td>
							<td className="num">{e.coins}</td>
						</tr>
					);
				})}
			</tbody>
		</table>
	);
}

function BossRow({ role, enemy, id, stage, chapterIndex, mult, scaling }: { role: string; enemy?: Enemy; id: string; stage: number; chapterIndex: number; mult?: { hp_mult: number; atk_mult: number }; scaling?: ScalingRules }) {
	if (!enemy) {
		return (
			<tr>
				<td>{role}</td>
				<td colSpan={5} style={{ color: "var(--color-bad)" }}>
					{id} is not in enemies.json
				</td>
			</tr>
		);
	}
	const ready = scaling && mult;
	return (
		<tr>
			<td>{role}</td>
			<td>
				<Link to="/monsters/$id" params={{ id }} className="underline decoration-[var(--color-ink-600)] underline-offset-2 hover:decoration-[var(--color-gold-400)]">
					{enemy.name}
				</Link>
				{enemy.cc_immune && (
					<span className="ml-2 text-xs" style={{ color: "var(--color-ink-400)" }}>
						CC immune
					</span>
				)}
			</td>
			<td className="num">{stage}</td>
			<td className="num">{ready ? num(scaledStat(enemy.hp, chapterIndex, stage, scaling) * mult.hp_mult) : "…"}</td>
			<td className="num">{ready ? num(scaledStat(enemy.atk, chapterIndex, stage, scaling) * mult.atk_mult, 1) : "…"}</td>
			<td className="num">{ready ? num(scaledStat(enemy.def, chapterIndex, stage, scaling), 1) : "…"}</td>
		</tr>
	);
}
